import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/* ------------------------------------------------------------------ */
/* Config                                                              */
/* ------------------------------------------------------------------ */

// TODO: replace with the exact hex used by the navbar pills so the page matches the site.
const BRAND = '#A5675B';

const DRAFT_KEY = 'scholarship_application_draft_v1';
const MAX_PHOTO_MB = 2;
const MAX_DOC_MB = 4;
const MAX_DOCS = 5;

// Order used to find the first field with an error
const FIELD_ORDER = [
  'photo', 'full_name', 'dob', 'gender', 'phone', 'aadhaar_number', 'email', 'address',
  'school_name', 'class_year', 'course', 'student_id', 'marks',
  'guardian_name', 'guardian_occupation', 'family_income', 'family_members', 'documents',
  'clubs', 'community_service', 'awards', 'importance', 'review_text', 'declared',
];

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function wordCount(text) {
  return (text.trim().match(/\b[\w']+\b/g) || []).length;
}

function errorText(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        const field = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join(' ') : '';
        return field ? `${field}: ${item.msg}` : item.msg || String(item);
      })
      .join(' | ');
  }
  return 'Something went wrong. Please try again.';
}

const fileSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const last10 = (phone) => String(phone || '').replace(/\D/g, '').slice(-10);
const formatAadhaar = (digits) => digits.replace(/(\d{4})(?=\d)/g, '$1 ');

// Verhoeff checksum used by Aadhaar numbers (format check only, not identity proof)
const V_D = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 2, 3, 4, 0, 6, 7, 8, 9, 5], [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
  [3, 4, 0, 1, 2, 8, 9, 5, 6, 7], [4, 0, 1, 2, 3, 9, 5, 6, 7, 8], [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
  [6, 5, 9, 8, 7, 1, 0, 4, 3, 2], [7, 6, 5, 9, 8, 2, 1, 0, 4, 3], [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
  [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
];
const V_P = [
  [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [1, 5, 7, 6, 2, 8, 3, 0, 9, 4], [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
  [8, 9, 1, 6, 0, 4, 3, 5, 2, 7], [9, 4, 5, 3, 1, 2, 6, 8, 7, 0], [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
  [2, 7, 9, 3, 8, 0, 6, 4, 1, 5], [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
];
function validAadhaar(num) {
  if (!/^[2-9]\d{11}$/.test(num)) return false;
  let c = 0;
  num.split('').reverse().forEach((ch, i) => {
    c = V_D[c][V_P[i % 8][Number(ch)]];
  });
  return c === 0;
}

function makeDefaults(user) {
  return {
    full_name: user?.name || '',
    dob: '',
    gender: '',
    aadhaar_number: '',
    phone: last10(user?.phone),
    email: user?.email || '',
    address: '',
    school_name: '',
    class_year: '',
    course: '',
    student_id: '',
    marks: '',
    guardian_name: '',
    guardian_occupation: '',
    family_income: '',
    family_members: '',
    clubs: '',
    community_service: '',
    awards: '',
    importance: '',
    review_text: '',
  };
}

// Draft is stored on this device only. The Aadhaar number, photo and documents are never saved.
function loadDraft() {
  try {
    const raw = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    if (!raw || typeof raw !== 'object') return null;
    const clean = Object.fromEntries(
      Object.entries(raw).filter(([k, v]) => k !== 'aadhaar_number' && String(v).trim() !== '')
    );
    return Object.keys(clean).length ? clean : null;
  } catch {
    return null;
  }
}
function saveDraft(form) {
  try {
    const { aadhaar_number, ...safe } = form; // eslint-disable-line no-unused-vars
    localStorage.setItem(DRAFT_KEY, JSON.stringify(safe));
    return true;
  } catch {
    return false;
  }
}
function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------------------------ */
/* Small UI pieces                                                     */
/* ------------------------------------------------------------------ */

const errorRing = 'border-red-500 focus-visible:ring-red-500';

function FieldMessage({ error, hint }) {
  if (error) return <p className="mt-1 text-xs text-red-600" role="alert">{error}</p>;
  if (hint) return <p className="mt-1 text-xs text-gray-500">{hint}</p>;
  return null;
}

function Required() {
  return <span className="text-red-600"> *</span>;
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl bg-white p-6 shadow-sm border border-gray-100 space-y-4">
      <div className="border-b border-gray-100 pb-2">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, name, error, hint, prefix, ...props }) {
  const input = (
    <Input
      id={`f-${name}`}
      name={name}
      aria-invalid={!!error}
      className={`${prefix ? 'rounded-l-none min-w-0' : ''} ${error ? errorRing : ''}`}
      {...props}
    />
  );
  return (
    <div>
      <Label htmlFor={`f-${name}`}>{label}<Required /></Label>
      <div className="mt-1">
        {prefix ? (
          <div className="flex">
            <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-gray-50 px-3 text-sm text-gray-600">
              {prefix}
            </span>
            {input}
          </div>
        ) : (
          input
        )}
      </div>
      <FieldMessage error={error} hint={hint} />
    </div>
  );
}

function AreaField({ label, name, error, hint, ...props }) {
  return (
    <div>
      <Label htmlFor={`f-${name}`}>{label}<Required /></Label>
      <Textarea
        id={`f-${name}`}
        name={name}
        aria-invalid={!!error}
        className={`mt-1 ${error ? errorRing : ''}`}
        {...props}
      />
      <FieldMessage error={error} hint={hint} />
    </div>
  );
}

function SelectField({ label, name, error, children, ...props }) {
  return (
    <div>
      <Label htmlFor={`f-${name}`}>{label}<Required /></Label>
      <select
        id={`f-${name}`}
        name={name}
        aria-invalid={!!error}
        className={`mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm ${error ? errorRing : ''}`}
        {...props}
      >
        {children}
      </select>
      <FieldMessage error={error} />
    </div>
  );
}

function WordCounter({ words, min, max }) {
  let tone = 'text-green-700';
  let note = '';
  if (words < min) {
    tone = 'text-amber-600';
    note = ` — ${min - words} more to reach the minimum`;
  } else if (words > max) {
    tone = 'text-red-600';
    note = ` — ${words - max} over the limit`;
  }
  return (
    <p className={`mt-1 text-xs ${tone}`}>
      {words} words (required: {min}–{max}){note}
    </p>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function ScholarshipApplyPage() {
  const { user, token } = useContext(AuthContext);

  const [draft] = useState(loadDraft);
  const [form, setForm] = useState(() => ({ ...makeDefaults(user), ...(draft || {}) }));
  const [draftNotice, setDraftNotice] = useState(!!draft);
  const [savedAt, setSavedAt] = useState(null);

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [documents, setDocuments] = useState([]);
  const [declared, setDeclared] = useState(false);

  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const photoInput = useRef(null);
  const docInput = useRef(null);

  const essayWords = useMemo(() => wordCount(form.importance), [form.importance]);
  const reviewWords = useMemo(() => wordCount(form.review_text), [form.review_text]);
  const authHeaders = { Authorization: `Bearer ${token}` };

  const clearError = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  const setValue = (key, value) => {
    setForm((c) => ({ ...c, [key]: value }));
    clearError(key);
  };
  const setField = (key) => (event) => setValue(key, event.target.value);
  const fp = (name) => ({ name, value: form[name], onChange: setField(name), error: errors[name] });

  // Autosave draft (device only)
  useEffect(() => {
    if (result) return undefined;
    const t = setTimeout(() => {
      if (saveDraft(form)) setSavedAt(new Date());
    }, 700);
    return () => clearTimeout(t);
  }, [form, result]);

  useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

  const startOver = () => {
    clearDraft();
    setForm(makeDefaults(user));
    setDraftNotice(false);
    setSavedAt(null);
    setErrors({});
  };

  const onPhoto = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      setErrors((p) => ({ ...p, photo: 'Photo must be a JPG or PNG image.' }));
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, photo: `Photo must be ${MAX_PHOTO_MB} MB or smaller.` }));
      return;
    }
    clearError('photo');
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const onDocuments = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    if (!picked.length) return;
    const okType = (f) => ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type);
    if (picked.some((f) => !okType(f))) {
      setErrors((p) => ({ ...p, documents: 'Only PDF, JPG or PNG files are allowed.' }));
      return;
    }
    if (picked.some((f) => f.size > MAX_DOC_MB * 1024 * 1024)) {
      setErrors((p) => ({ ...p, documents: `Each file must be ${MAX_DOC_MB} MB or smaller.` }));
      return;
    }
    if (documents.length + picked.length > MAX_DOCS) {
      setErrors((p) => ({ ...p, documents: `You can upload up to ${MAX_DOCS} files.` }));
      return;
    }
    clearError('documents');
    setDocuments((cur) => [...cur, ...picked]);
  };
  const removeDocument = (index) => setDocuments((cur) => cur.filter((_, i) => i !== index));

  const aadhaarComplete = form.aadhaar_number.length === 12;
  const aadhaarLiveError = aadhaarComplete && !validAadhaar(form.aadhaar_number)
    ? "This Aadhaar number doesn't look valid. Please check the digits."
    : '';

  const validate = () => {
    const e = {};
    const required = [
      'full_name', 'dob', 'gender', 'address', 'school_name', 'class_year', 'course', 'student_id', 'marks',
      'guardian_name', 'guardian_occupation', 'clubs', 'community_service', 'awards',
    ];
    required.forEach((k) => {
      if (!String(form[k]).trim()) e[k] = 'This field is required';
    });
    if (!e.dob && new Date(form.dob) > new Date()) e.dob = 'Date of birth cannot be in the future';
    if (!/^[6-9]\d{9}$/.test(form.phone)) e.phone = 'Enter a valid 10-digit mobile number';
    if (!validAadhaar(form.aadhaar_number)) e.aadhaar_number = 'Enter a valid 12-digit Aadhaar number';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Enter a valid email address';
    if (form.family_income === '' || Number(form.family_income) < 0) e.family_income = 'Enter the annual household income';
    const members = Number(form.family_members);
    if (!Number.isInteger(members) || members < 1) e.family_members = 'Enter the number of family members (at least 1)';
    if (!photo) e.photo = 'Please upload your photo';
    if (!documents.length) e.documents = 'Please upload at least one supporting document';
    if (essayWords < 200 || essayWords > 300) e.importance = `Write 200–300 words (currently ${essayWords})`;
    if (reviewWords < 100 || reviewWords > 200) e.review_text = `Write 100–200 words (currently ${reviewWords})`;
    if (!declared) e.declared = 'Please accept the declaration';
    return e;
  };

  const focusFirstError = (e) => {
    const first = FIELD_ORDER.find((k) => e[k]);
    if (!first) return;
    const el = document.getElementById(`f-${first}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    setServerError('');
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      setTimeout(() => focusFirstError(found), 50);
      return;
    }

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => data.append(key, value));
    data.append('confirm', 'true');
    data.append('photo', photo);
    documents.forEach((file) => data.append('documents', file));

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/scholarship/apply`, data, { headers: authHeaders });
      clearDraft();
      setResult(response.data);
      window.scrollTo({ top: 0 });
    } catch (err) {
      setServerError(errorText(err));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  /* ---- success screen: next steps ---- */
  if (result) {
    const number = result.application_number;
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="max-w-xl mx-auto px-4 py-12">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-gray-900">Application form saved</h1>
            <p className="mt-2 text-sm text-gray-600">Your application number</p>
            <p className="text-3xl font-semibold tracking-wide" style={{ color: BRAND }} data-testid="scholarship-application-number">
              {number}
            </p>
            <p className="mt-2 text-xs text-gray-500">
              {result.email_sent || result.sms_sent
                ? 'We also sent this number to you by email or SMS.'
                : 'Please note this number. We could not send an email or SMS just now.'}
            </p>
          </div>

          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
            <h2 className="font-semibold text-gray-900">Your application is not complete yet</h2>
            <ol className="mt-3 list-decimal pl-5 space-y-2 text-sm text-gray-700">
              <li>Download the printout of your application.</li>
              <li>Sign it, and get your school or college to stamp and sign it.</li>
              <li>Upload a scan or clear photo of the attested copy, or post the original to us.</li>
            </ol>
            <p className="mt-3 text-xs text-gray-500">We review your application only after the attested copy reaches us.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: BRAND }}>
                <Link to={`/scholarship/attest?number=${number}`}>Preview and download application</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to={`/scholarship/status?number=${number}`}>Track status</Link>
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const errorCount = Object.values(errors).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-semibold text-gray-900">Scholarship application</h1>
        <p className="mt-2 text-sm text-gray-600">
          You can submit even if you have not rented a book yet. Approval waits until one rental is completed,
          and scholars keep at least one rental each quarter. All fields are mandatory.
        </p>

        <div className="mt-5 rounded-lg border p-4 text-sm text-gray-800" style={{ borderColor: `${BRAND}55`, backgroundColor: `${BRAND}12` }}>
          <p className="font-medium">How it works</p>
          <ol className="mt-1 list-decimal pl-5 space-y-0.5 text-gray-700">
            <li>Fill in this form and submit it (about 10 minutes). The page to download your application opens right after you submit.</li>
            <li>Download the printout, sign it, and get it stamped and signed by your school or college.</li>
            <li>Upload the attested copy, or post it to us. We review it after it reaches us.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-600">Keep ready: Aadhaar number, student ID, a passport-size photo, and scans or photos of your income proof and marks card. Your progress is saved on this device as you type.</p>
        </div>

        {draftNotice && (
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm text-gray-700">
            <span>We restored your saved draft. Please enter your Aadhaar number and upload your photo and documents again.</span>
            <button type="button" className="text-xs underline text-gray-500 hover:text-gray-800" onClick={startOver}>
              Start over
            </button>
          </div>
        )}

        {serverError && (
          <p className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert" data-testid="scholarship-apply-error">
            {serverError}
          </p>
        )}

        <form onSubmit={submit} noValidate className="mt-6 space-y-6">
          {/* 1. Personal details */}
          <Section title="Personal Details">
            <div className="flex flex-col sm:flex-row gap-5 items-start">
              <div className="shrink-0" id="f-photo" tabIndex={-1}>
                <Label>Student Photo<Required /></Label>
                <div
                  className={`mt-1 h-44 w-36 rounded-md border border-dashed overflow-hidden flex items-center justify-center bg-gray-50 text-xs text-gray-400 ${errors.photo ? 'border-red-500' : 'border-gray-400'}`}
                >
                  {photoPreview ? <img src={photoPreview} alt="Student" className="h-full w-full object-cover" /> : 'No photo'}
                </div>
                <input ref={photoInput} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onPhoto} data-testid="scholarship-photo" />
                <Button type="button" variant="outline" size="sm" className="mt-2 w-36" onClick={() => photoInput.current?.click()}>
                  {photo ? 'Change photo' : 'Upload photo'}
                </Button>
                {photo ? <p className="mt-1 w-36 truncate text-xs text-gray-600" title={photo.name}>{photo.name}</p> : null}
                <div className="w-36">
                  <FieldMessage
                    error={errors.photo}
                    hint={`Passport-size, face clearly visible, plain background. JPG/PNG, up to ${MAX_PHOTO_MB} MB.`}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 flex-1 w-full">
                <Field label="Full Name" {...fp('full_name')} />
                <Field label="Date of Birth" type="date" max={new Date().toISOString().slice(0, 10)} {...fp('dob')} />
                <SelectField label="Gender" {...fp('gender')}>
                  <option value="">Select</option>
                  <option>Female</option>
                  <option>Male</option>
                  <option>Other</option>
                  <option>Prefer not to say</option>
                </SelectField>
                <Field
                  label="Phone Number"
                  prefix="+91"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10-digit mobile number"
                  name="phone"
                  value={form.phone}
                  error={errors.phone}
                  onChange={(e) => setValue('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field
                label="Aadhaar Number"
                name="aadhaar_number"
                inputMode="numeric"
                maxLength={14}
                placeholder="1234 5678 9012"
                value={formatAadhaar(form.aadhaar_number)}
                onChange={(e) => setValue('aadhaar_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
                error={errors.aadhaar_number || aadhaarLiveError}
                hint="Checked by our team during review."
                data-testid="scholarship-aadhaar"
              />
              <Field
                label="Email Address"
                type="email"
                placeholder="name@example.com"
                hint="All correspondence about your application will be sent to this email address. Please enter it carefully."
                {...fp('email')}
              />
            </div>

            <AreaField label="Home Address" rows={3} {...fp('address')} />
          </Section>

          {/* 2. Educational details */}
          <Section title="Educational Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Current School or College" {...fp('school_name')} />
              <Field label="Current Grade / Year of Study" {...fp('class_year')} />
              <Field label="Degree / Major / Course" {...fp('course')} />
              <Field label="Student ID Number" {...fp('student_id')} />
              <Field label="Latest Grade / GPA / Percentage" {...fp('marks')} />
            </div>
          </Section>

          {/* 3. Family and financial details */}
          <Section title="Family and Financial Details">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Parent / Guardian Name" {...fp('guardian_name')} />
              <Field label="Parent / Guardian Occupation" {...fp('guardian_occupation')} />
              <Field label="Total Annual Household Income (₹)" type="number" min="0" inputMode="numeric" {...fp('family_income')} />
              <Field label="Number of Family Members" type="number" min="1" inputMode="numeric" {...fp('family_members')} />
            </div>

            <div id="f-documents" tabIndex={-1}>
              <Label>Supporting Documents (income proof, marks card, ID proof)<Required /></Label>
              <input
                ref={docInput}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                multiple
                className="hidden"
                onChange={onDocuments}
                data-testid="scholarship-documents"
              />
              <div className="mt-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={documents.length >= MAX_DOCS}
                  onClick={() => docInput.current?.click()}
                  className={errors.documents ? errorRing : ''}
                >
                  {documents.length ? 'Add more files' : 'Choose files'}
                </Button>
              </div>
              {documents.length > 0 && (
                <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200 bg-white text-sm">
                  {documents.map((file, i) => (
                    <li key={`${file.name}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2">
                      <span className="truncate">{file.name} <span className="text-xs text-gray-500">({fileSize(file.size)})</span></span>
                      <button
                        type="button"
                        className="text-lg leading-none text-gray-400 hover:text-red-600"
                        aria-label={`Remove ${file.name}`}
                        onClick={() => removeDocument(i)}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <FieldMessage
                error={errors.documents}
                hint={`PDF, JPG or PNG. Up to ${MAX_DOCS} files, ${MAX_DOC_MB} MB each.`}
              />
            </div>
          </Section>

          {/* 4. Extracurricular */}
          <Section title="Extracurricular Activities">
            <AreaField label="Clubs / Sports / Leadership Roles" rows={3} hint="Write “None” if not applicable." {...fp('clubs')} />
            <AreaField label="Community Service / Volunteering" rows={3} hint="Write “None” if not applicable." {...fp('community_service')} />
            <AreaField label="Awards or Honors Received" rows={3} hint="Write “None” if not applicable." {...fp('awards')} />
          </Section>

          {/* 5. Essay */}
          <Section title="Short Essay (200–300 words)">
            <AreaField
              label="Why is this scholarship important for you, and how will it help you achieve your career goals?"
              rows={9}
              data-testid="scholarship-essay"
              {...fp('importance')}
            />
            <WordCounter words={essayWords} min={200} max={300} />
          </Section>

          {/* 6. Book review */}
          <Section title="Short Essay (100–200 words)">
            <AreaField
              label="Write a short review of the book you rented from mybook.rent"
              rows={6}
              data-testid="scholarship-review-text"
              {...fp('review_text')}
            />
            <WordCounter words={reviewWords} min={100} max={200} />
          </Section>

          {/* 7. Declaration */}
          <Section title="Application Declaration">
            <div>
              <label className="flex items-start gap-2 text-sm text-gray-800">
                <input
                  id="f-declared"
                  type="checkbox"
                  className="mt-1"
                  checked={declared}
                  onChange={(e) => {
                    setDeclared(e.target.checked);
                    clearError('declared');
                  }}
                  data-testid="scholarship-declaration"
                />
                I hereby certify that all the information provided above is true, complete and correct to the best of my knowledge.
              </label>
              <FieldMessage error={errors.declared} />
            </div>
            <p className="text-xs text-gray-500">
              You will sign and date the printed copy by hand, and your school or college will attest it, after you submit this form.
            </p>
          </Section>

          {/* Submit */}
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <p className="text-sm text-gray-600">
              Please review your details before submitting. You will not be able to edit the application after it is submitted. After you click Submit, the next page lets you download your application and upload the school-attested copy.
            </p>
            {errorCount > 0 && (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {errorCount} {errorCount === 1 ? 'field needs' : 'fields need'} your attention. They are highlighted above.
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto h-12 px-10 text-base text-white hover:opacity-90"
                style={{ backgroundColor: BRAND }}
                data-testid="scholarship-submit"
              >
                {submitting ? 'Submitting…' : 'Submit application'}
              </Button>
              {savedAt && (
                <span className="text-xs text-gray-500">
                  Draft saved on this device at {savedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}
